import { Link } from 'react-router-dom';

export function Landing() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-primary-light to-surface py-20 md:py-32 overflow-hidden">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-text mb-6 leading-tight">
              Trích xuất dữ liệu hóa đơn{' '}
              <span className="text-primary">bằng AI Vision</span>
            </h1>
            <p className="text-lg md:text-xl text-text-muted mb-8 max-w-2xl mx-auto">
              Ứng dụng desktop tự động hóa nhập liệu hóa đơn. Nhanh, chính xác, bảo mật.
              Hỗ trợ nhiều định dạng, tích hợp Google Sheets, thanh toán SePay QR.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register" className="btn-primary text-lg px-8 py-3 w-full sm:w-auto">
                Bắt đầu miễn phí
              </Link>
              <Link to="/pricing" className="btn-outline text-lg px-8 py-3 w-full sm:w-auto">
                Xem bảng giá
              </Link>
            </div>
            <p className="mt-4 text-sm text-text-muted">
              Không cần thẻ tín dụng · Dùng thử 7 ngày gói Pro
            </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-surface">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text mb-4">
              Tính năng nổi bật
            </h2>
            <p className="text-lg text-text-muted max-w-2xl mx-auto">
              Công cụ mạnh mẽ cho doanh nghiệp và kế toán viên
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="card p-6 h-full">
                <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-text mb-2">{feature.title}</h3>
                <p className="text-text-muted">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text mb-4">
              Cách thức hoạt động
            </h2>
            <p className="text-lg text-text-muted max-w-2xl mx-auto">
              3 bước đơn giản để tự động hóa nhập liệu hóa đơn
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={step.title} className="relative">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold text-xl z-10">
                  {index + 1}
                </div>
                <div className="card p-6 pt-16 h-full">
                  <h3 className="text-xl font-semibold text-text mb-2 text-center">{step.title}</h3>
                  <p className="text-text-muted text-center">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-5 left-[calc(50%+6px)] right-[calc(50%+6px)] h-0.5 bg-primary/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Sẵn sàng tự động hóa nhập liệu?
          </h2>
          <p className="text-lg text-primary-light mb-8 max-w-2xl mx-auto">
            Tham gia hàng nghìn người dùng đã tiết kiệm hàng giờ làm việc mỗi tuần
          </p>
          <Link to="/register" className="btn-primary bg-white text-primary text-lg px-8 py-3 hover:bg-gray-100">
            Tạo tài khoản miễn phí
          </Link>
        </div>
      </section>
    </div>
  );
}

const features = [
  {
    title: 'AI Vision đa mô hình',
    description: 'Hỗ trợ Gemini 2.5, GPT-4o, Claude 3.5 Sonnet. Chọn mô hình phù hợp nhất cho từng loại hóa đơn.',
    icon: function AIIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    },
  },
  {
    title: 'Tích hợp Google Sheets',
    description: 'Xuất dữ liệu trực tiếp ra Google Sheets. Tự động tạo cột, định dạng, sẵn sàng cho kế toán.',
    icon: function SheetsIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 18h18M3 6h18M9 4v16M15 4v16" />
        </svg>
      );
    },
  },
  {
    title: 'Thanh toán SePay QR',
    description: 'Nâng cấp gói Pro/Enterprise bằng QR code SePay. Xác thực tự động, kích hoạt ngay lập tức.',
    icon: function QRIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h4v4H4V4zm0 6h4v4H4v-4zm0 6h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4zm6-12h4v4h-4V4zm0 6h4v4h-4v-4zm0 6h4v4h-4v-4z" />
        </svg>
      );
    },
  },
  {
    title: 'Xử lý hàng loạt',
    description: 'Upload nhiều hóa đơn cùng lúc. Xử lý song song, theo dõi tiến trình realtime, xuất kết quả một lần.',
    icon: function BatchIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    },
  },
  {
    title: 'Lịch sử & Tìm kiếm',
    description: 'Lưu trữ toàn bộ lịch sử OCR. Tìm kiếm theo ngày, nhà cung cấp, số tiền, từ khóa. Phân loại tự động.',
    icon: function SearchIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      );
    },
  },
  {
    title: 'Bảo mật & Riêng tư',
    description: 'Dữ liệu hóa đơn được xử lý cục bộ trên máy. Chỉ gửi ảnh đến AI provider, không lưu trữ trên server.',
    icon: function ShieldIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      );
    },
  },
];

const steps = [
  {
    title: 'Tải ảnh hóa đơn',
    description: 'Kéo thả hoặc chọn file ảnh/PDF hóa đơn. Hỗ trợ nhiều file cùng lúc.',
  },
  {
    title: 'AI xử lý tự động',
    description: 'Mô hình vision trích xuất: số hóa đơn, ngày, nhà cung cấp, dòng mục, thuế, tổng tiền.',
  },
  {
    title: 'Xuất & Tích hợp',
    description: 'Xem kết quả, sửa nếu cần, xuất ra Excel/CSV hoặc đồng bộ thẳng vào Google Sheets.',
  },
];